import type { IntercomProps } from "react-use-intercom";

interface BrowserIntercom {
  (command: string, ...args: unknown[]): void;
  q?: unknown[];
}
type IntercomWindow = Window & {
  Intercom?: BrowserIntercom;
  intercomSettings?: Record<string, unknown>;
};

export function hasIntercomInstance(): boolean {
  return typeof (window as IntercomWindow).Intercom === "function";
}

/** Wait for the actual script, not its queue stub. Never queue a JWT into an unloaded SDK. */
export function waitForIntercom(signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    let poll: ReturnType<typeof setTimeout>;
    const deadline = Date.now() + 15_000;
    const finish = (loaded: boolean) => {
      clearTimeout(poll);
      signal.removeEventListener("abort", cancel);
      if (loaded) resolve();
      else reject(new Error("Support unavailable"));
    };
    const cancel = () => finish(false);
    const check = () => {
      if (signal.aborted) return finish(false);
      const sdk = (window as IntercomWindow).Intercom;
      if (typeof sdk === "function" && !Array.isArray(sdk.q)) return finish(true);
      if (Date.now() >= deadline) return finish(false);
      poll = setTimeout(check, 100);
    };
    signal.addEventListener("abort", cancel, { once: true });
    check();
  });
}

/** react-use-intercom guards repeat boot calls; promotion/recovery must preserve guest cookies. */
export function rebootIntercom(appId: string, props: IntercomProps): void {
  const intercomWindow = window as IntercomWindow;
  const settings = {
    ...props.customAttributes,
    app_id: appId,
    hide_default_launcher: false,
    ...(props.userId ? { user_id: props.userId, intercom_user_jwt: props.intercomUserJwt } : {}),
  };
  intercomWindow.intercomSettings = settings;
  intercomWindow.Intercom?.("boot", settings);
}

export function hasIntercomSession(appId: string): boolean {
  try {
    return document.cookie.split(";").some(cookie => cookie.trim().startsWith(`intercom-session-${appId}=`));
  } catch { return false; }
}

/** shutdown leaves the anonymous ID cookie behind; remove it too when an identified session ends. */
export function clearIntercomIdentity(appId: string): void {
  try {
    const names = document.cookie.split(";").map(cookie => cookie.trim().split("=")[0])
      .filter(name => name.startsWith("intercom-") && name.endsWith(`-${appId}`));
    const hostParts = window.location.hostname.split(".");
    const domains = ["", ...hostParts.map((_, index) => `; Domain=${hostParts.slice(index).join(".")}`)];
    const pathParts = window.location.pathname.split("/");
    const paths = new Set(["/", ...pathParts.map((_, index) => pathParts.slice(0, index + 1).join("/") || "/")]);
    for (const name of names) {
      for (const domain of domains) {
        for (const path of paths) {
          document.cookie = `${name}=; Max-Age=0; Path=${path}${domain}`;
        }
      }
    }
  } catch { /* Storage restrictions must not prevent Plutus logout. */ }
  delete (window as IntercomWindow).intercomSettings;
}
